import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { clsx } from '../../ds/clsx';
import { Avatar, Eyebrow } from '../../ds/components';
import type { QueueContactCard } from './api';
import styles from './ContactCard.module.css';

export interface ContactCardRow {
  label: string;
  value: ReactNode;
}

export interface ContactCardProps {
  name: string;
  initials: string;
  /** „Kunde · Deal WE 2.05 · Stage Beratung" / „Call gebucht · kein Deal" */
  roleLine: string;
  rows: ReadonlyArray<ContactCardRow>;
  /** The contact record the name links to. */
  href?: string | undefined;
  className?: string | undefined;
}

/**
 * „Kontakt" card of the focus mode and the appointment detail (Queue 03–09): avatar, name, role line and
 * label/value rows — the row set varies per contact (A5), so the caller passes what applies.
 */
export function ContactCard({ name, initials, roleLine, rows, href, className }: ContactCardProps) {
  return (
    <div className={clsx(styles.card, className)}>
      <Eyebrow>Kontakt</Eyebrow>
      <div className={styles.head}>
        <Avatar initials={initials} name={name} size={40} tone="sand" className={styles.avatar} />
        <div className={styles.headText}>
          <div className={styles.name}>
            {href ? (
              <Link to={href} className={styles.nameLink}>
                {name}
              </Link>
            ) : (
              name
            )}
          </div>
          <div className={styles.roleLine}>{roleLine}</div>
        </div>
      </div>
      <dl className={styles.rows}>
        {rows.map((row) => (
          <div key={row.label} className={styles.row}>
            <dt className={styles.label}>{row.label}</dt>
            <dd className={styles.value}>{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** The rows of a queue contact card: phone (or e-mail), advisor, deal, last contact, no-shows. */
export function queueContactRows(contact: QueueContactCard): ContactCardRow[] {
  const rows: ContactCardRow[] = [];
  if (contact.phone !== null) rows.push({ label: 'Telefon', value: contact.phone });
  else if (contact.email !== null) rows.push({ label: 'E-Mail', value: contact.email });
  if (contact.advisor !== null) rows.push({ label: 'Betreuer', value: contact.advisor.name });
  if (contact.deal !== null) rows.push({ label: 'Deal', value: contact.deal.label });
  if (contact.lastContact !== null) rows.push({ label: 'Letzter Kontakt', value: contact.lastContact });
  if (contact.noShowCount > 0) rows.push({ label: 'Bisherige No-Shows', value: String(contact.noShowCount) });
  return rows;
}
